import type { MachineEtatOptions, Runnable, TransitionOut } from '@/features/domaine/machine-etats'
import { useMachineEtatDisplay } from '@/features/interface/machine-etats/useMachineEtat'

import ChoixModeRattachement from './cartes/ChoixModeRattachement.vue'
import ChoixContextRattachement from './cartes/ChoixContextRattachement.vue'
import ChoixTypeEntiteJuridiqueRattachement from './cartes/ChoixTypeEntiteJuridiqueRattachement.vue'
import ChoixIdccRattachement from './cartes/ChoixIdccRattachement.vue'
import ConfirmerDemandeRattachement from './cartes/ConfirmerDemandeRattachement.vue'
import RefuserDemandeRattachement from './cartes/RefuserDemandeRattachement.vue'
import SaisieCode from './cartes/SaisieCode.vue'
import RefuserSaisieCode from './cartes/RefuserSaisieCode.vue'

import type { DemandeRattachementLaunch, DemandeRattachementResult } from './types'
import type { ContextAppType } from '@/features/auth/context-app/types'
import type { DemandeRattachementAdherentDTO, EntiteAdherentDTO } from '@/features/api/extranet/dto/Rattachements'
import {
  buildPostDemandeRattachement,
  buildSearch,
  postValidationCode,
} from '@/features/services/profil/RattachementInscriptionService'
import ChoixSirenSiret from '@/features/interface/profil-utilisateur/rattachements/demande/cartes/ChoixSirenSiret.vue'
import InfoErreurEntiteNonTrouvee from '@/features/interface/profil-utilisateur/rattachements/demande/cartes/InfoErreurEntiteNonTrouvee.vue'
import { CODES_POSTAUX_DROM } from '@/features/domaine/constantes'
import { isNullUndefinedOrEmptyString } from '@/features/shared/tools'

const ETAT_CONTEXT_SELECTIONNÉ = 'ETAT_CONTEXT_SELECTIONNÉ' as const
const transitionSelectionnerContextRattachements: Runnable<
  ContextAppType,
  Pick<DemandeRattachementResult, 'context'>
> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixContextRattachement, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<DemandeRattachementResult>
  },
}

const ETAT_MODE_RATTACHEMENT_SELECTIONNÉ = 'ETAT_MODE_RATTACHEMENT_SELECTIONNÉ' as const
const transitionSelectionnerModeRattachements: Runnable<void, Pick<DemandeRattachementResult, 'modeRattachement'>> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixModeRattachement, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<DemandeRattachementResult>
  },
}

/* Flux Code */
const ETAT_CODE_SAISI = 'ETAT_CODE_SAISI' as const
const transitionSaisieCode: Runnable<ContextAppType, Pick<DemandeRattachementResult, 'code' | 'codeSirenSiret'>> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: SaisieCode, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<DemandeRattachementResult>
  },
}

const ETAT_CODE_SAISIE_VERIFIÉE = 'ETAT_CODE_SAISIE_VERIFIÉE' as const
const transitionVerifieCode: Runnable<
  Pick<DemandeRattachementResult, 'code' | 'codeSirenSiret' | 'context'>,
  Pick<DemandeRattachementResult, 'codeEstValidé'>
> = {
  run: async (options) => {
    const demande = options.modelIn
    const codeEstValidé = await postValidationCode({
      code: demande.code!,
      siren: demande.codeSirenSiret!.substring(0, 9),
      nic: demande.codeSirenSiret!.substring(9, 14) || null,
      typeEntreprise: demande.context === 'adherent' ? 'Adherent' : 'Prestataire',
    })

    return {
      out: { codeEstValidé },
      transitionState: null,
      type: 'automatique',
    }
  },
}

const ETAT_CODE_REFUSÉ = 'ETAT_CODE_REFUSÉ' as const
const transitionNotifieCodeRefusé: Runnable<void, void> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: RefuserSaisieCode, ...options })

    return {
      out: void 0,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<void>
  },
}

/* Flux Siren / Siret */
const ETAT_TYPE_ENTITE_JURIDIQUE_SELECTIONNÉE = 'ETAT_TYPE_ENTITE_JURIDIQUE_SELECTIONNÉE' as const
const transitionSelectionnerTypeEntiteJuridique: Runnable<void, Pick<DemandeRattachementResult, 'typeEntite'>> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixTypeEntiteJuridiqueRattachement, ...options })
    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_SIREN_SIRET_SELECTIONNÉ = 'ETAT_SIREN_SIRET_SELECTIONNÉ' as const
const transitionSelectionnerSirenSiret: Runnable<
  Pick<DemandeRattachementResult, 'typeEntite'>,
  Pick<DemandeRattachementResult, 'sirenSiret'>
> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixSirenSiret, ...options })
    console.log('transitionSelectionnerSirenSiret', result.value)
    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<DemandeRattachementResult>
  },
}

async function getEntiteJuridiqueInfo(sirenSiret: string, context: ContextAppType) {
  const searchAsync = buildSearch(context)
  const entitésJuridiques = await searchAsync(sirenSiret, { sablier: true, dialogMode: 'retry' })

  if (sirenSiret.length === 14) {
    // Traite établissements
    const entite = entitésJuridiques.find((e) => sirenSiret === `${e.siren}${e.nic}`)
    if (!entite) return null
    const existeCpDrom =
      !isNullUndefinedOrEmptyString(entite.adresse?.codePostal) &&
      CODES_POSTAUX_DROM.some((pattern) => entite.adresse.codePostal?.startsWith(pattern))
    return {
      entite,
      adresseEntite: entite,
      existeCpDrom,
    }
  } else {
    // Traite entreprise
    const entite = entitésJuridiques.find((e) => !e.nic && e.siren === sirenSiret)
    if (!entite) return null
    const adresseEntite = entitésJuridiques.find((e) => e.estEtablissementSiege)
    const existeCpDrom = entitésJuridiques.some(
      (e) => e.adresse?.codePostal && CODES_POSTAUX_DROM.some((pattern) => e.adresse.codePostal?.startsWith(pattern)),
    )
    return {
      entite,
      adresseEntite,
      existeCpDrom,
    }
  }
}

const ETAT_ENTITE_JURIDIQUE_CHARGÉE = 'ETAT_ENTITE_JURIDIQUE_CHARGÉE' as const
const transitionChargementEntitéJuridique: Runnable<
  Pick<DemandeRattachementResult, 'context' | 'typeEntite' | 'sirenSiret'>,
  Pick<DemandeRattachementResult, 'entite' | 'adresseEntite' | 'existeEtablissementDROM' | 'entiteEstValidée'>
> = {
  run: async (options) => {
    // Chargement des entités juridiques
    const infosEntite = await getEntiteJuridiqueInfo(options.modelIn.sirenSiret!, options.modelIn.context!)

    // Fin si l'entité n'a pas été trouvée
    if (!infosEntite)
      return {
        out: {},
        transitionState: null,
        type: 'automatique',
      }

    // Calcule le retour
    const demande = {
      entite: infosEntite.entite,
      adresseEntite: infosEntite.adresseEntite,
    } as DemandeRattachementResult

    if ('certificationQualiopi' in infosEntite.entite) {
      // CAS PRESTATIRE
      demande.entiteEstValidée = (infosEntite.entite.certificationQualiopi?.length ?? 0) > 0
    } else {
      // CAS ADHERENT
      demande.entiteEstValidée =
        infosEntite.entite.estChampsAkto === true ||
        infosEntite.entite.avecAuMoinsUnIdcc === true ||
        infosEntite.entite.avecAuMoinsUnUnique === true
      demande.existeEtablissementDROM = infosEntite.existeCpDrom
    }

    return {
      out: demande,
      transitionState: null,
      type: 'automatique',
    }
  },
}

const ETAT_ENTITE_NON_TROUVEE = 'ETAT_ENTITE_NON_TROUVEE' as const
const transitionInfoErreurEntitéNonTrouvée: Runnable<
  Pick<DemandeRattachementResult, 'sirenSiret' | 'typeEntite'>,
  void
> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    await display.runComponent({ component: InfoErreurEntiteNonTrouvee, ...options })

    return {
      out: void 0,
      transitionState: null,
      type: 'interactif',
    }
  },
}

const ETAT_IDCC_SELECTIONNÉ = 'ETAT_IDCC_SELECTIONNÉ' as const
const transitionSelectionnerIdcc: Runnable<string, Pick<DemandeRattachementResult, 'idcc' | 'idccEstValidé'>> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixIdccRattachement, ...options })

    const demande = result.value as DemandeRattachementResult
    demande.idccEstValidé = (demande.idcc?.perimetres.length ?? 0) > 0

    return {
      out: demande,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<DemandeRattachementResult>
  },
}

const ETAT_DEMANDE_RATTACHEMENT_CONFIRMÉE = 'ETAT_DEMANDE_RATTACHEMENT_CONFIRMÉE' as const
const transitionConfirmerDemandeRattachement: Runnable<
  Pick<DemandeRattachementResult, 'context'>,
  Pick<DemandeRattachementResult, 'demandeRattachementConfirmée'>
> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ConfirmerDemandeRattachement, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_DEMANDE_RATTACHEMENT_REFUSÉE = 'ETAT_DEMANDE_RATTACHEMENT_REFUSÉE' as const
const transitionNotifierRefusDemandeRattachement: Runnable<
  void,
  Pick<DemandeRattachementResult, 'demandeRattachementConfirmée'>
> = {
  run: async (options) => {
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: RefuserDemandeRattachement, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<DemandeRattachementResult>
  },
}

const ETAT_DEMANDE_RATTACHEMENT_TERMINÉE = 'ETAT_DEMANDE_RATTACHEMENT_TERMINÉE' as const
const transitionTraiterDemandeRattachement: Runnable<
  DemandeRattachementResult,
  Pick<DemandeRattachementResult, 'demandeRattachementTraitée'>
> = {
  run: async (options) => {
    const idcc =
      'idcc' in options.modelIn.entite!
        ? (options.modelIn?.idcc?.code ?? options.modelIn?.entite?.idcc?.code ?? null)
        : null

    const payload: DemandeRattachementAdherentDTO = {
      siren: options.modelIn.entite!.siren,
      nic: options.modelIn.entite!.nic,
      idcc,
    }
    const api = buildPostDemandeRattachement(options.modelIn.context!)
    const demandeRattachementTraitée = await api(payload, { sablier: true, dialogMode: 'retry' })
    return {
      out: { demandeRattachementTraitée },
      transitionState: null,
      type: 'automatique',
    }
  },
}

export const DemandeRattachementConfiguration: MachineEtatOptions<
  DemandeRattachementLaunch,
  DemandeRattachementResult
> = {
  transitions: [
    {
      etatDepart: null,
      etatArrive: ETAT_CONTEXT_SELECTIONNÉ,
      runnable: transitionSelectionnerContextRattachements,
    },
    {
      etatDepart: ETAT_CONTEXT_SELECTIONNÉ,
      etatArrive: ETAT_MODE_RATTACHEMENT_SELECTIONNÉ,
      runnable: transitionSelectionnerModeRattachements,
      queryIn: (options) => fusionneEtats<DemandeRattachementResult>(options.etats).context,
    },
    {
      // Point entré process CODE
      etatDepart: ETAT_MODE_RATTACHEMENT_SELECTIONNÉ,
      etatArrive: ETAT_CODE_SAISI,
      runnable: transitionSaisieCode,
      if: (options) => fusionneEtats<DemandeRattachementResult>(options.etats).modeRattachement === 'code',
      queryIn: (options) => fusionneEtats<DemandeRattachementResult>(options.etats).context,
    },
    {
      etatDepart: ETAT_CODE_SAISI,
      etatArrive: ETAT_CODE_SAISIE_VERIFIÉE,
      runnable: transitionVerifieCode,
      queryIn: (options) => fusionneEtats<DemandeRattachementResult>(options.etats),
    },
    {
      etatDepart: ETAT_CODE_SAISIE_VERIFIÉE,
      etatArrive: ETAT_CODE_REFUSÉ,
      runnable: transitionNotifieCodeRefusé,
      if: (options) => fusionneEtats<DemandeRattachementResult>(options.etats).codeEstValidé === false,
    },
    {
      etatDepart: ETAT_MODE_RATTACHEMENT_SELECTIONNÉ,
      etatArrive: ETAT_TYPE_ENTITE_JURIDIQUE_SELECTIONNÉE,
      runnable: transitionSelectionnerTypeEntiteJuridique,
      if: (options) => fusionneEtats<DemandeRattachementResult>(options.etats).modeRattachement === 'siren-siret',
    },
    {
      etatDepart: ETAT_TYPE_ENTITE_JURIDIQUE_SELECTIONNÉE,
      etatArrive: ETAT_SIREN_SIRET_SELECTIONNÉ,
      runnable: transitionSelectionnerSirenSiret,
      queryIn: (options) => {
        const etats = fusionneEtats<DemandeRattachementResult>(options.etats)
        return {
          context: etats.context,
          typeEntite: etats.typeEntite,
        }
      },
    },
    {
      etatDepart: ETAT_SIREN_SIRET_SELECTIONNÉ,
      etatArrive: ETAT_ENTITE_JURIDIQUE_CHARGÉE,
      runnable: transitionChargementEntitéJuridique,
      queryIn: (options) => {
        const etats = fusionneEtats<DemandeRattachementResult>(options.etats)
        return {
          context: etats.context,
          typeEntite: etats.typeEntite,
          sirenSiret: etats.sirenSiret,
        } as Pick<DemandeRattachementResult, 'context' | 'typeEntite' | 'sirenSiret'>
      },
    },
    {
      etatDepart: ETAT_ENTITE_JURIDIQUE_CHARGÉE,
      etatArrive: ETAT_ENTITE_NON_TROUVEE,
      runnable: transitionInfoErreurEntitéNonTrouvée,
      if: (options) => !fusionneEtats<DemandeRattachementResult>(options.etats).entite,
      queryIn: (options) => {
        const etats = fusionneEtats<DemandeRattachementResult>(options.etats)
        return {
          typeEntite: etats.typeEntite,
          sirenSiret: etats.sirenSiret,
        } as Pick<DemandeRattachementResult, 'typeEntite' | 'sirenSiret'>
      },
    },
    {
      etatDepart: ETAT_ENTITE_JURIDIQUE_CHARGÉE,
      etatArrive: ETAT_IDCC_SELECTIONNÉ,
      runnable: transitionSelectionnerIdcc,
      if: (options) => {
        const etats = fusionneEtats<DemandeRattachementResult>(options.etats)
        if (etats.context === 'prestataire') return false
        return (etats.entite as EntiteAdherentDTO).estChampsAkto === null
      },
      queryIn: (options) => {
        const etats = fusionneEtats<DemandeRattachementResult>(options.etats)
        const entite = etats.entite as EntiteAdherentDTO // Jamais appelé en mode Prestataire

        // Cas établissement
        if (entite?.nic) return etats.entite?.adresse?.codePostal

        // Traite les entreprises en OPCO Unique
        if (entite.avecAuMoinsUnUnique) return '97600'

        // Traite les entreprises sur les DROM
        if (etats.existeEtablissementDROM) return `${CODES_POSTAUX_DROM.at(0)}00000`.substring(0, 5)

        return '75000'
      },
    },
    {
      etatDepart: ETAT_IDCC_SELECTIONNÉ,
      etatArrive: ETAT_DEMANDE_RATTACHEMENT_REFUSÉE,
      runnable: transitionNotifierRefusDemandeRattachement,
      if: (options) => {
        // Uniquement adhérent
        const demande = fusionneEtats<DemandeRattachementResult>(options.etats)
        return !demande.entiteEstValidée && !demande.idccEstValidé
      },
    },
    {
      etatDepart: ETAT_ENTITE_JURIDIQUE_CHARGÉE,
      etatArrive: ETAT_DEMANDE_RATTACHEMENT_CONFIRMÉE,
      runnable: transitionConfirmerDemandeRattachement,
      if: (options) => {
        // uniquement prestataire
        const demande = fusionneEtats<DemandeRattachementResult>(options.etats)
        if (demande.context === 'adherent') return false
        return !demande.entiteEstValidée
      },
      queryIn: (_) => ({ context: 'prestataire' }),
    },
    {
      etatDepart: ETAT_IDCC_SELECTIONNÉ,
      etatArrive: ETAT_DEMANDE_RATTACHEMENT_CONFIRMÉE,
      runnable: transitionConfirmerDemandeRattachement,
      if: (options) => {
        // Uniquement adhérent
        const demande = fusionneEtats<DemandeRattachementResult>(options.etats)
        if (demande.entiteEstValidée) {
          return (demande.entite as EntiteAdherentDTO).estChampsAkto === null
        } else if (demande.idccEstValidé) {
          return demande.existeEtablissementDROM === true
        } else {
          return false
        }
      },
      queryIn: (_) => ({ context: 'adherent' }),
    },
    {
      // Création demande rattachement
      etatDepart: [ETAT_ENTITE_JURIDIQUE_CHARGÉE, ETAT_IDCC_SELECTIONNÉ, ETAT_DEMANDE_RATTACHEMENT_CONFIRMÉE],
      etatArrive: ETAT_DEMANDE_RATTACHEMENT_TERMINÉE,
      runnable: transitionTraiterDemandeRattachement,
      if: (options) => {
        const demande = fusionneEtats<DemandeRattachementResult>(options.etats)
        return (
          demande.entiteEstValidée === true ||
          demande.idccEstValidé === true ||
          demande.demandeRattachementConfirmée === true
        )
      },
      queryIn: (options) => fusionneEtats<DemandeRattachementResult>(options.etats),
    },
  ],
  resultCallback: (options) => {
    // console.log(fusionneEtats<DemandeRattachementResult>(options.etats))
    return fusionneEtats<DemandeRattachementResult>(options.etats)
  },
  disposeCallback: () => useMachineEtatDisplay().hide(),
}

function fusionneEtats<T extends object>(etats: Record<string, T>) {
  return Object.values(etats).reduce((result, etat) => {
    return { ...result, ...etat }
  })
}
